import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Workspace.css';

/**
 * SRS workspace — WORK MODE on one scene.
 *
 *   header   ‹ Scenes (drawer) · breadcrumb · prev/next · scene title · path strip
 *   NOW      the current step: its question, the model's read, the proposals to decide
 *   SETTLED  what is already locked (by stage), inherited context, open items, source
 *   DISCUSS  conversation scoped to the step — or to one row, via a card's Discuss button
 *
 * The ledger IS the authorization model: the model can only create `proposed` rows; the
 * writer's Accept/Choose is the only path to `locked`.
 */

const post = (url, data, opts = {}) => router.post(url, data, { preserveScroll: true, ...opts });

const STAGE_GROUPS = {
  baseline: [{ types: ['current_state'], title: 'What the scene does now', hint: 'evidence from the pages — not a preservation command' }],
  target: [
    { types: ['target'], title: 'What must this scene accomplish?', hint: 'independent of how the draft does it' },
    { types: ['protected_outcome', 'essential_context', 'story_constraint'], title: 'Keep, whatever mechanism we choose', hint: 'usually nothing — only what a redesign could silently lose', emptyText: 'Nothing to protect — the model found nothing a redesign could silently lose.' },
  ],
  approach: [{ types: ['mechanism'], title: 'Which mechanism produces the targets?', hint: 'the current draft is one candidate — choose one; the others are set aside', exclusive: true }],
  source_map: [{ types: ['source_disposition'], title: 'What happens to the existing material', hint: 'by function, under the chosen mechanism' }],
  d2: [{ types: ['d2_blueprint'], title: 'The blueprint', hint: 'only commitments that would change the story if omitted' }],
  d1: [{ types: ['d1_realization'], title: 'The pages', hint: 'from the approved design, reusing what still works' }],
  verify: [{ types: ['decision'], title: 'Verification', hint: 'the pages checked against every lock' }],
};

const ADD_DEFAULT = { baseline: 'current_state', target: 'target', approach: 'mechanism', source_map: 'source_disposition', d2: 'd2_blueprint', d1: 'd1_realization', verify: 'decision' };

/* ───────────────────────────── scenes drawer ───────────────────────────── */

function TreeNodes({ nodes, activeId, uuid }) {
  return (
    <ul>
      {nodes.map((n) => (
        <li key={n.id} className={`grain-${n.grain}`}>
          <Link href={n.grain === 'script' ? `/srs/p/${uuid}/contract` : `/srs/p/${uuid}/${n.id}`} className={n.id === activeId ? 'active' : ''}>
            <span className="srs-node-title">{n.label}</span>
            {n.counts.proposed > 0 && <span className="badge text-bg-warning">{n.counts.proposed}</span>}
            {n.counts.blocking > 0 && <span className="badge text-bg-danger">{n.counts.blocking}</span>}
            {n.counts.locked > 0 && <span className="badge text-bg-secondary">{n.counts.locked}</span>}
          </Link>
          {n.children?.length > 0 && <TreeNodes nodes={n.children} activeId={activeId} uuid={uuid} />}
        </li>
      ))}
    </ul>
  );
}

function ScenesDrawer({ tree, activeId, uuid, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="srs-drawer-backdrop" onClick={onClose}>
      <aside className="srs-drawer" onClick={(e) => e.stopPropagation()} aria-label="Scenes">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h2 className="h6 srs-label mb-0">Scenes</h2>
          <span className="d-flex gap-2 align-items-center">
            <Link href={`/srs/p/${uuid}/scenes`} className="small">All scenes, with progress</Link>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
          </span>
        </div>
        <div className="small text-muted mb-2 d-flex flex-wrap gap-2">
          <span><span className="badge text-bg-warning">n</span> to decide</span>
          <span><span className="badge text-bg-danger">n</span> blocking</span>
          <span><span className="badge text-bg-secondary">n</span> locked</span>
        </div>
        <div className="srs-tree">
          <TreeNodes nodes={tree} activeId={activeId} uuid={uuid} />
        </div>
      </aside>
    </div>
  );
}

/* ───────────────────────────── path strip ───────────────────────────── */

/* Writer language first; the spec term stays visible as a secondary label while the ontology is in development. */
function StageName({ s }) {
  return (
    <span className="srs-stage-name">
      {s.number ? <span className="srs-path-no">{s.number}</span> : null}
      <span>{s.writer_label}</span>
      <span className="srs-spec">{s.label}</span>
    </span>
  );
}

function PathStrip({ path, onPick, checking = false }) {
  // While the writer is still checking step 1's readings, step 1 is the current step.
  const shown = checking
    ? path.map((s) => (s.key === 'baseline' ? { ...s, state: 'current' } : s.key === 'target' ? { ...s, state: 'pending' } : s))
    : path;
  return (
    <ol className="srs-path">
      {shown.map((s) => (
        <li key={s.key} className={s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
          {s.state === 'done' ? (
            <button type="button" className="srs-path-btn" onClick={() => onPick(s.key)}>
              <span className="srs-path-dot" aria-hidden="true" /><StageName s={s} />
            </button>
          ) : (
            <><span className="srs-path-dot" aria-hidden="true" /><StageName s={s} /></>
          )}
        </li>
      ))}
    </ol>
  );
}

/* The upstream locks the current stage must satisfy — kept next to the choice. */
function CriteriaStrip({ criteria, stageWriterLabel }) {
  const [open, setOpen] = useState({});
  if (!criteria?.length) return null;
  return (
    <div className="srs-criteria">
      <div className="srs-criteria-head">Must satisfy <span className="srs-meta">({criteria.length} lock{criteria.length === 1 ? '' : 's'} upstream of "{stageWriterLabel}")</span></div>
      <ul>
        {criteria.map((c) => {
          const long = (c.body || '').length > 160;
          const isOpen = !!open[c.id];
          return (
            <li key={c.id}>
              <span className="srs-type">{c.type_label}</span>
              <span className="srs-criteria-title">{c.title}</span>
              {c.status === 'provisional' && <span className="srs-meta"> · provisional</span>}
              <div className="srs-criteria-body">
                {long && !isOpen ? `${c.body.slice(0, 160)}… ` : c.body}
                {long && (
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setOpen((o) => ({ ...o, [c.id]: !isOpen }))}>
                    {isOpen ? 'less' : 'more'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ───────────────────────────── structured bodies ───────────────────────────── */

const SECTION_LABELS = ['Mechanism', 'Strengths', 'Costs', 'Source impact', 'Differs from'];

/** A mechanism body written as labelled lines → sections; anything else → null (render as prose). */
function parseSections(body) {
  if (!body) return null;
  // Older rows carry the labels inline in one paragraph; break before each label so they split too.
  const normalized = body.replace(/\s+(?=(Strengths|Costs|Source impact|Differs from)\b[^:]*:)/g, '\n');
  const lines = normalized.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const out = [];
  let matched = 0;
  lines.forEach((line) => {
    const label = SECTION_LABELS.find((l) => line.toLowerCase().startsWith(l.toLowerCase()));
    if (label) {
      matched++;
      const rest = line.slice(label.length).replace(/^[^:]*:\s*/, '');
      const head = label === 'Differs from' ? line.slice(0, line.indexOf(':') > 0 ? line.indexOf(':') : label.length) : label;
      out.push({ head, text: rest });
    } else if (out.length) {
      out[out.length - 1].text += ' ' + line;
    } else {
      out.push({ head: null, text: line });
    }
  });
  return matched >= 2 ? out : null;
}

function StructuredBody({ body }) {
  const sections = parseSections(body);
  if (!sections) return <div className="srs-body">{body}</div>;
  return (
    <div className="srs-sections">
      {sections.map((s, i) => (
        <div key={i} className={`srs-section ${s.head ? s.head.toLowerCase().replace(/[^a-z]+/g, '-') : 'lead'}`}>
          {s.head && <span className="srs-section-head">{s.head}: </span>}
          <span className="srs-section-text">{s.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────── one card ───────────────────────────── */

function Card({ row, uuid, busy, setBusy, onDiscuss, onLocked, choose = false, compact = false, evidence = false, chip = null, chipSpec = null }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(row.title || '');
  const [body, setBody] = useState(row.body || '');
  const [blocking, setBlocking] = useState(!!row.blocking);
  const [resolution, setResolution] = useState('');
  const [resolving, setResolving] = useState(false);
  const isBusy = busy === row.id;
  const isOpenItem = row.kind === 'open';
  const isProposed = row.status === 'proposed';
  const isBinding = row.status === 'locked' || row.status === 'provisional';

  const act = (path, data = {}, onSuccess) => {
    setBusy(row.id);
    post(`/srs/p/${uuid}/a/${row.id}/${path}`, data, { onSuccess, onFinish: () => setBusy(null) });
  };

  const accept = () => {
    const data = {};
    if (editing) { data.title = title; data.body = body; }
    if (isOpenItem) data.blocking = blocking;
    if (choose) data.exclusive = true;
    act('confirm', data, () => onLocked?.(row, choose ? 'Chosen' : isOpenItem ? 'Open item kept' : 'Locked'));
  };

  const save = () => {
    const data = { title, body };
    if (isOpenItem) data.blocking = blocking;
    act('update', data);
    setEditing(false);
  };

  const classes = ['srs-card', row.status, row.recommended && isProposed ? 'recommended' : '',
    isOpenItem && row.blocking && row.status === 'open' ? 'blocking' : '', compact ? 'compact' : '', choose ? 'candidate' : ''].join(' ');
  const isCurrent = row.type === 'mechanism' && /^CURRENT\b/i.test(row.title || '');
  const shownTitle = isCurrent ? (row.title || '').replace(/^CURRENT:\s*/i, '') : row.title;

  return (
    <div className={classes}>
      {row.recommended && isProposed && (
        <div className="srs-reco">★ Recommended{row.rationale ? <span className="srs-reco-why"> — {row.rationale}</span> : null}</div>
      )}
      <div className={choose || chip !== null ? 'srs-card-stack' : 'd-flex align-items-start gap-2'}>
        {chip === false ? null : chip ? (
          <span className="srs-facet-head">
            <span className="srs-facet-label">{chip}</span>
            {chipSpec && <span className="srs-spec">{chipSpec}</span>}
          </span>
        ) : (
          <span className="srs-type">{isCurrent ? 'Current draft' : row.type_label}</span>
        )}
        <div className="flex-grow-1">
          {editing ? (
            <>
              <input className="form-control form-control-sm mb-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short handle" />
              <textarea className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)} />
            </>
          ) : (
            <>
              {shownTitle && <div className="fw-semibold">{shownTitle}</div>}
              {row.type === 'mechanism' ? <StructuredBody body={row.body} /> : <div className="srs-body">{row.body}</div>}
            </>
          )}
          {(row.anchored_to || row.threatened_by) && (
            <div className="srs-anchor mt-1">
              {row.threatened_by && <span>threatened by: {row.threatened_by}</span>}
              {row.threatened_by && row.anchored_to && <span className="mx-2">·</span>}
              {row.anchored_to && <span>anchored to: {row.anchored_to}</span>}
            </div>
          )}
          {isOpenItem && (isProposed || row.status === 'open') && (
            <div className="form-check mt-1">
              <input className="form-check-input" type="checkbox" id={`blk-${row.id}`} checked={blocking}
                onChange={(e) => { setBlocking(e.target.checked); if (!isProposed && !editing) act('update', { blocking: e.target.checked }); }} />
              <label className="form-check-label" htmlFor={`blk-${row.id}`}>Render must wait for this</label>
            </div>
          )}
          {row.resolution && <div className="mt-1"><span className="srs-label">Resolved:</span> {row.resolution}</div>}
          <div className="srs-meta mt-1">
            {evidence ? (row.status === 'locked' ? 'reading · confirmed by you' : 'reading from the page') : (
              <>
                {isBinding && <>v{row.version} · </>}
                {row.status === 'cancelled' && row.type === 'mechanism' ? 'set aside' : row.status === 'locked' ? 'decided' : row.status}
                {row.authorized_by === 'writer' ? ' by you' : row.authorized_by === 'model' ? ' by model' : ''}
                {row.supersedes_id ? ` · replaces #${row.supersedes_id}` : ''}
              </>
            )}
            {` · #${row.id}`}
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
        {isBusy && <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="working" />}
        {!isBusy && isProposed && (
          <>
            <button className="btn btn-sm btn-primary" onClick={accept}>
              {choose ? 'Choose' : isOpenItem ? 'Accept as open' : 'Accept'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing((v) => !v)}>{editing ? 'Stop editing' : 'Edit'}</button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>
            <button className="btn btn-sm btn-link text-danger ms-auto" onClick={() => act('cancel')}>Reject</button>
          </>
        )}
        {!isBusy && !isProposed && (isBinding || row.status === 'open') && (
          <>
            {editing ? (
              <>
                <button className="btn btn-sm btn-primary" onClick={save}>Save{isBinding ? ' as new version' : ''}</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(false); setTitle(row.title || ''); setBody(row.body || ''); }}>Cancel</button>
              </>
            ) : (
              <>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(true)}>{evidence ? 'Fix' : 'Edit'}</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>
              </>
            )}
            {row.status === 'open' && !editing && (
              resolving ? (
                <div className="d-flex gap-2 flex-grow-1">
                  <input className="form-control form-control-sm" placeholder="How was it resolved?" value={resolution} onChange={(e) => setResolution(e.target.value)} />
                  <button className="btn btn-sm btn-success" onClick={() => act('resolve', { resolution })}>Resolve</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setResolving(false)}>Back</button>
                </div>
              ) : (
                <button className="btn btn-sm btn-outline-success" onClick={() => setResolving(true)}>Resolve</button>
              )
            )}
            {!editing && (
              <button className="btn btn-sm btn-link text-danger ms-auto" onClick={() => act('cancel')}>{evidence ? 'Remove' : isBinding ? 'Retire' : 'Drop'}</button>
            )}
          </>
        )}
        {!isBusy && !isProposed && !isBinding && row.status !== 'open' && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── add your own ───────────────────────────── */

function AddRow({ types, uuid, unitId, defaultType, label }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(defaultType || 'story_constraint');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [provisional, setProvisional] = useState(false);
  const [threatenedBy, setThreatenedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const t = types.find((x) => x.key === type);

  useEffect(() => { if (defaultType) setType(defaultType); }, [defaultType]);

  const submit = (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    post(`/srs/p/${uuid}/u/${unitId}/artifacts`, { type, title, body, blocking, provisional, threatened_by: threatenedBy }, {
      onSuccess: () => { setTitle(''); setBody(''); setBlocking(false); setProvisional(false); setThreatenedBy(''); setOpen(false); },
      onFinish: () => setSaving(false),
    });
  };

  if (!open) {
    const dt = types.find((x) => x.key === defaultType);
    return (
      <button className="btn btn-outline-primary btn-sm" onClick={() => setOpen(true)}>
        {label ? `+ ${label}` : `+ Add your own${dt ? ` ${dt.label.toLowerCase()}` : ''}`}
      </button>
    );
  }

  return (
    <form className="srs-card" onSubmit={submit}>
      <div className="row g-2">
        <div className="col-md-5">
          <label className="form-label srs-label mb-1" htmlFor="srs-add-type">What kind of row</label>
          <select id="srs-add-type" className="form-select form-select-sm" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          {t && <div className="srs-meta mt-1">{t.desc}</div>}
        </div>
        <div className="col-md-7">
          <label className="form-label srs-label mb-1" htmlFor="srs-add-title">Short handle</label>
          <input id="srs-add-title" className="form-control form-control-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="col-12">
          <label className="form-label srs-label mb-1" htmlFor="srs-add-body">The commitment, in full</label>
          <textarea id="srs-add-body" className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)} required />
        </div>
        {type === 'protected_outcome' && (
          <div className="col-12">
            <label className="form-label srs-label mb-1" htmlFor="srs-add-threat">What kind of redesign would lose it</label>
            <input id="srs-add-threat" className="form-control form-control-sm" value={threatenedBy} onChange={(e) => setThreatenedBy(e.target.value)} />
          </div>
        )}
        <div className="col-12 d-flex flex-wrap align-items-center gap-3">
          {t?.kind === 'open' ? (
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="srs-add-blocking" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} />
              <label className="form-check-label" htmlFor="srs-add-blocking">Render must wait for this</label>
            </div>
          ) : (
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="srs-add-prov" checked={provisional} onChange={(e) => setProvisional(e.target.checked)} />
              <label className="form-check-label" htmlFor="srs-add-prov">Provisional (working assumption, not a lock)</label>
            </div>
          )}
          <div className="ms-auto d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (t?.kind === 'open' ? 'Add open item' : (provisional ? 'Add as provisional' : 'Lock it'))}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

/* ───────────────────────────── NOW pane ───────────────────────────── */

/* ───────────────────────────── step 1: the readings, one rung at a time ───────────────────────────── */

/*
 * Which readings exist, which rung is up, whether the walk is done. The rungs (purpose → transition →
 * mechanism) are confirmed one at a time, top down; the other facets are evidence for later steps and
 * are only shown folded. A rung is confirmed when its latest row is `locked` (by the writer).
 */
const CONFLICT_FACETS = ['purpose', 'transition', 'mechanism', 'grain'];

function readingState(ledger, facets) {
  const rows = [...ledger.locked, ...ledger.provisional].filter((r) => r.type === 'current_state');
  const rungKeys = facets.filter((f) => f.rung).map((f) => f.key);
  const expFacet = facets.find((f) => f.experience) || null;
  const expKey = expFacet?.key || 'experience';
  const latest = (facet) => {
    const locked = rows.filter((r) => r.facet === facet && r.status === 'locked');
    if (locked.length) return locked[locked.length - 1];
    const prov = rows.filter((r) => r.facet === facet && r.status === 'provisional');
    return prov.length ? prov[prov.length - 1] : null;
  };
  const rungs = rungKeys.map((key) => ({ facet: facets.find((f) => f.key === key), row: latest(key) }));
  const activeIndex = rungs.findIndex((r) => !r.row || r.row.status !== 'locked');
  const order = facets.map((f) => f.key);
  const secondary = rows
    .filter((r) => !rungKeys.includes(r.facet) && r.facet !== expKey)
    .sort((a, b) => (order.indexOf(a.facet) === -1 ? 900 : order.indexOf(a.facet)) - (order.indexOf(b.facet) === -1 ? 900 : order.indexOf(b.facet)) || a.id - b.id);
  // The audience reading is not a fourth rung: it was made from the pages alone, so it can
  // contradict any of the three. It is confirmed the same way and it blocks the same way.
  const expRow = expFacet ? latest(expKey) : null;
  const rungsConfirmed = rungs.length > 0 && activeIndex === -1;
  const expConfirmed = expRow?.status === 'locked';
  const conflict = (ledger.open || []).find(
    (r) => r.type === 'open_item' && CONFLICT_FACETS.includes(r.facet),
  ) || null;

  return {
    rows, rungs, activeIndex, secondary,
    expFacet, expRow, rungsConfirmed, expConfirmed, conflict,
    hasRungs: rungs.length > 0,
    allConfirmed: rungsConfirmed && (!expFacet || expConfirmed),
    hasReadings: rows.length > 0,
  };
}

/* Is the writer still walking step 1's rungs? (Readings exist, nothing pending, a rung unconfirmed.) */
function isChecking(step, ledger, facets) {
  const rs = readingState(ledger, facets);
  // `baseline` comes back as the current stage when the two readings disagree — the walk is
  // reopened, not restarted.
  const atStepOne = step.current === 'target' || step.current === 'baseline';
  return atStepOne && ledger.proposed.length === 0 && rs.hasRungs && rs.hasReadings
    && (!rs.allConfirmed || !!rs.conflict);
}

/* The claim itself. Some readings are labelled lines: entry→exit, and the audience reading. */
const LABELLED = {
  transition: /^(Entry|Exit)\s*[:—-]\s*(.*)$/i,
  experience: /^(Coming in|The turn|Leaving)\s*[:—-]\s*(.*)$/i,
};
function ReadingBody({ row, large = false }) {
  const cls = large ? 'srs-rung-claim' : 'srs-body';
  const pattern = LABELLED[row.facet];
  if (pattern) {
    const lines = (row.body || '').split('\n').map((l) => l.trim()).filter(Boolean)
      .map((l) => { const m = l.match(pattern); return m ? { k: m[1], t: m[2] } : { k: null, t: l }; });
    if (lines.some((x) => x.k)) {
      return (
        <div className={large ? 'srs-entry-exit' : 'srs-entry-exit small'}>
          {lines.map((x, i) => <div key={i}>{x.k && <span className="srs-section-head">{x.k} · </span>}{x.t}</div>)}
        </div>
      );
    }
  }
  return <div className={cls}>{row.body}</div>;
}

/*
 * One rung of the walk. `state`: done (confirmed, collapsed to its handle) · active (the one question on
 * screen) · pending (below the active one — named, not shown). Fixing a reading confirms it with the
 * writer's words; the caller then re-reads whatever is still the model's below it.
 */
function RungCard({ rung, uuid, unitId, state, busy, setBusy, onDiscuss, onFixed, rereading = false, openEdit = false }) {
  const { facet, row } = rung;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // The conflict panel sends the writer straight here, to the reading it says is in dispute.
  useEffect(() => { if (openEdit && row) { setOpen(true); setEditing(true); } }, [openEdit, row?.id]);
  const [title, setTitle] = useState(row?.title || '');
  const [body, setBody] = useState(row?.body || '');
  useEffect(() => { setTitle(row?.title || ''); setBody(row?.body || ''); setEditing(false); }, [row?.id]);
  const isBusy = row ? busy === row.id : busy === `add-${facet.key}`;
  const confirmed = row?.status === 'locked';

  // "That's right" — the reading becomes the writer's.
  const confirm = () => {
    setBusy(row.id);
    post(`/srs/p/${uuid}/a/${row.id}/confirm`, {}, { onFinish: () => setBusy(null) });
  };
  // "No — fix it": a provisional reading is confirmed with the writer's words; a confirmed one gets a new version.
  const save = () => {
    if (!body.trim()) return;
    setBusy(row.id);
    const path = confirmed ? 'update' : 'confirm';
    post(`/srs/p/${uuid}/a/${row.id}/${path}`, { title, body }, {
      onSuccess: () => { setEditing(false); onFixed?.(facet.key, row); },
      onFinish: () => setBusy(null),
    });
  };
  // The model left this rung out: the writer writes it.
  const add = () => {
    if (!body.trim()) return;
    setBusy(`add-${facet.key}`);
    post(`/srs/p/${uuid}/u/${unitId}/artifacts`, { type: 'current_state', facet: facet.key, title, body }, { onFinish: () => setBusy(null) });
  };

  if (state === 'pending') {
    return (
      <div className="srs-rung pending">
        <span className="srs-rung-then">then</span>
        <span className="srs-facet-label">{facet.label}</span>
        <span className="srs-spec">{facet.chip}</span>
        {rereading && <span className="srs-meta ms-auto">re-reading…</span>}
      </div>
    );
  }

  if (state === 'done' && !open) {
    return (
      <div className="srs-rung done">
        <span className="srs-rung-tick" aria-hidden="true">✓</span>
        <span className="srs-facet-label">{facet.label}</span>
        <span className="srs-spec">{facet.chip}</span>
        <span className="srs-rung-handle">{row.title || row.body}</span>
        <button type="button" className="btn btn-sm btn-link srs-rung-change" onClick={() => setOpen(true)}>show</button>
      </div>
    );
  }

  const form = (
    <>
      <input className="form-control form-control-sm mb-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Handle (a few words)" aria-label="Handle" />
      <textarea className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder={facet.key === 'transition' ? 'Entry: …\nExit: …'
          : facet.key === 'experience' ? 'Coming in: …\nThe turn: …\nLeaving: …'
            : 'The reading, in one sentence'} aria-label="The reading" />
    </>
  );

  return (
    <div className={`srs-card srs-rung-card ${state}`}>
      <div className="srs-facet-head">
        <span className="srs-facet-label">{facet.label}</span>
        <span className="srs-spec">{facet.chip}</span>
        {confirmed && <span className="srs-meta">confirmed by you</span>}
      </div>

      {!row ? (
        <>
          <div className="srs-meta mt-1 mb-2">The model didn't read this rung. Write it yourself:</div>
          {form}
          <div className="d-flex gap-2 mt-2">
            <button className="btn btn-primary" onClick={add} disabled={isBusy || !body.trim()}>{isBusy ? 'Saving…' : "That's the reading"}</button>
          </div>
        </>
      ) : editing ? (
        <>
          <div className="mt-1">{form}</div>
          <div className="d-flex gap-2 mt-2">
            <button className="btn btn-primary" onClick={save} disabled={isBusy || !body.trim()}>{isBusy ? 'Saving…' : "Save — that's the reading"}</button>
            <button className="btn btn-outline-secondary" onClick={() => { setEditing(false); setTitle(row.title || ''); setBody(row.body || ''); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <ReadingBody row={row} large />
          {row.anchored_to && <div className="srs-anchor mt-1">{row.anchored_to}</div>}
          {rereading && !confirmed ? (
            <div className="d-flex align-items-center gap-2 mt-2 srs-meta">
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="re-reading" />
              Re-reading from your corrected reading…
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
              {isBusy && <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="working" />}
              {!isBusy && !confirmed && <button className="btn btn-primary" onClick={confirm}>That's right</button>}
              {!isBusy && <button className="btn btn-outline-secondary" onClick={() => setEditing(true)}>{confirmed ? 'Change it' : 'No — fix it'}</button>}
              {!isBusy && <button className="btn btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>}
              {state === 'done' && <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => setOpen(false)}>Hide</button>}
              <span className="srs-meta ms-auto">#{row.id}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/*
 * The two readings of the scene disagree. Neither is senior to the other — the walk asks about
 * the ladder first only because the other questions cannot be phrased without it — so the writer
 * is not told which one is wrong, only that one of them is.
 */
function ConflictPanel({ conflict, labels, uuid, unitId, onFix, onDiscuss, onSpan, canSpan }) {
  const parts = {};
  (conflict.body || '').split('\n').map((l) => l.trim()).filter(Boolean).forEach((l) => {
    const m = l.match(/^(Confirmed|The pages|Where it shows)\s*[:—-]\s*(.*)$/i);
    if (m) parts[m[1].toLowerCase()] = m[2]; else parts.rest = [parts.rest, l].filter(Boolean).join(' ');
  });
  const about = labels?.[conflict.facet] || 'this scene';
  const isGrain = conflict.facet === 'grain';

  return (
    <div className="srs-card srs-conflict">
      <div className="srs-facet-head">
        <span className="srs-facet-label">These two can&rsquo;t both be right</span>
        <span className="srs-spec">about {about}</span>
      </div>

      <div className="srs-conflict-side">
        <span className="srs-section-head">You confirmed</span>
        <div>{parts.confirmed || conflict.title}</div>
      </div>
      <div className="srs-conflict-side">
        <span className="srs-section-head">Read on its own, the page says</span>
        <div>{parts['the pages'] || parts.rest}</div>
      </div>
      {parts['where it shows'] && (
        <div className="srs-anchor mt-1">Where it shows: {parts['where it shows']}</div>
      )}

      <div className="srs-conflict-ask">One of these is the wrong reading of the scene.</div>

      <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
        {isGrain ? (
          canSpan && (
            <button className="btn btn-primary" onClick={onSpan}>Read it with the next scene as one unit</button>
          )
        ) : (
          <button className="btn btn-primary" onClick={() => onFix(conflict.facet)}>Fix {about}</button>
        )}
        <button className="btn btn-outline-secondary" onClick={() => onFix('experience')}>Fix the audience read</button>
        <button className="btn btn-outline-secondary" onClick={() => onDiscuss(conflict)}>Discuss</button>
        <span className="srs-meta ms-auto">#{conflict.id}</span>
      </div>
      <div className="srs-meta mt-2">
        If the scene is really doing two things at once, say so in Discuss &mdash; naming the second
        transaction is what makes it one.
      </div>
    </div>
  );
}

/*
 * The readings. `walk` (step 1): rungs one at a time, the evidence facets folded.
 * `settled` (Decided so far): the rungs as compact cards, the evidence facets folded.
 */
function Readings({ ledger, facets, uuid, unitId, busy, setBusy, onDiscuss, onLocked, onFixed,
  rereading = false, mode = 'walk', conflictLabels = {}, experienceRunning = false, checkRunning = false,
  onExperience, onSpan, canSpan = false }) {
  const rs = readingState(ledger, facets);
  const meta = (r) => facets.find((f) => f.key === r.facet);
  const [editFacet, setEditFacet] = useState(null);
  useEffect(() => { setEditFacet(null); }, [rs.conflict?.id]);
  if (mode === 'settled') {
    const settled = [...rs.rungs, ...(rs.expFacet ? [{ facet: rs.expFacet, row: rs.expRow }] : [])];
    return settled.filter((r) => r.row).map((r) => (
      <Card key={r.row.id} row={r.row} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onLocked={onLocked}
        compact evidence chip={r.facet.label} chipSpec={r.facet.chip} />
    ));
  }

  const expCard = rs.expFacet && (
    <RungCard rung={{ facet: rs.expFacet, row: rs.expRow }} uuid={uuid} unitId={unitId} busy={busy} setBusy={setBusy}
      onDiscuss={onDiscuss} onFixed={onFixed} openEdit={editFacet === 'experience'}
      state={rs.expConfirmed ? 'done' : 'active'} />
  );

  return (
    <div className="srs-walk">
      {rs.rungs.map((r, i) => (
        <RungCard key={r.facet.key} rung={r} uuid={uuid} unitId={unitId} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onFixed={onFixed} rereading={rereading}
          openEdit={editFacet === r.facet.key}
          state={rs.activeIndex === -1 || i < rs.activeIndex ? 'done' : i === rs.activeIndex ? 'active' : 'pending'} />
      ))}

      {rs.rungsConfirmed && rs.expFacet && (
        <>
          <div className="srs-walk-turn">
            <span className="srs-walk-turn-k">and one more, read on its own</span>
            Those three are what the scene is for. This one is what it does to the person watching &mdash;
            read off the pages alone, without the three above, so that it is able to disagree with them.
          </div>

          {experienceRunning && !rs.expRow ? (
            <div className="d-flex align-items-center gap-3 my-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="reading" />
              <span className="text-muted">Watching the scene&hellip;</span>
            </div>
          ) : !rs.expRow ? (
            <div className="srs-card srs-rung-card active">
              <div className="srs-meta mb-2">This reading hasn&rsquo;t been made yet.</div>
              <button className="btn btn-primary" onClick={onExperience}>Read what it does to us</button>
            </div>
          ) : checkRunning ? (
            <>
              {expCard}
              <div className="d-flex align-items-center gap-2 mt-2 srs-meta">
                <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="checking" />
                Checking this against the three readings above&hellip;
              </div>
            </>
          ) : rs.conflict ? (
            <>
              <ConflictPanel conflict={rs.conflict} labels={conflictLabels} uuid={uuid} unitId={unitId}
                onFix={setEditFacet} onDiscuss={onDiscuss} onSpan={onSpan} canSpan={canSpan} />
              {editFacet === 'experience' && expCard}
            </>
          ) : expCard}
        </>
      )}
    </div>
  );
}

/* ───────────────────────────── the scene: context for every step ───────────────────────────── */

/*
 * The pages and what was read off them are the context every step works against — not part of
 * any one rung or step — so they sit at the top, on every step. The pages scroll in a fixed box;
 * the evidence readings (handed forward · who drives it · what the audience must learn · beats)
 * sit beside them, folded.
 */
function SceneContext({ active, ledger, facets, uuid, busy, setBusy, onDiscuss }) {
  const rs = readingState(ledger, facets);
  const meta = (r) => facets.find((f) => f.key === r.facet);
  const hasEvidence = rs.secondary.length > 0;
  return (
    <section className="srs-scene">
      <div className="row g-3">
        <div className={hasEvidence ? 'col-12 col-lg-7' : 'col-12'}>
          <details className="srs-details srs-scene-pages" open>
            <summary className="srs-label">The pages, as written</summary>
            <div className="srs-source srs-source-top mt-2">{active.source || '(no source text on file)'}</div>
          </details>
        </div>
        {hasEvidence && (
          <div className="col-12 col-lg-5">
            <details className="srs-details srs-scene-evidence">
              <summary className="srs-label">
                Also read from the page · {rs.secondary.length}
                <span className="srs-group-hint ms-2">{rs.secondary.map((r) => (meta(r)?.label || 'reading').toLowerCase()).join(' · ')}</span>
              </summary>
              <div className="srs-meta mb-2">Evidence the later steps use — not confirmed, fix or remove what is wrong.</div>
              <div className="srs-scene-evidence-list">
                {rs.secondary.map((r) => (
                  <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss}
                    compact evidence chip={meta(r)?.label || 'Reading'} chipSpec={meta(r)?.chip || 'Current state'} />
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    </section>
  );
}

function NowPane({ step, ledger, active, uuid, unitId, nextUnit, types, readingFacets, conflictLabels, busy, setBusy, onDiscuss, onLocked, onShowStage }) {
  const [running, setRunning] = useState(false);
  const [rereading, setRereading] = useState(false);
  const [watching, setWatching] = useState(false);
  const [provisional, setProvisional] = useState(false);
  const { next, current, number, total, current_label, current_why, decide_hint, criteria, read,
    needs_baseline, needs_experience, needs_conflict_check, run_in_flight } = step;
  const info = step.step;
  const checking = isChecking(step, ledger, readingFacets);
  const rs = readingState(ledger, readingFacets);
  // The page was (re)opened while a run is still going on the server: this step's, or a re-read of step 1.
  const inFlight = !running && !rereading && run_in_flight !== null && run_in_flight !== undefined
    && (run_in_flight === current || (checking && run_in_flight === 'baseline'));
  const working = running || inFlight;
  const stepOneInFlight = checking && inFlight && run_in_flight === 'baseline';
  // Below the ladder it is a re-read; once the ladder is settled it is the audience reading.
  const rereadInFlight = rereading || (stepOneInFlight && !rs.rungsConfirmed);
  const expInFlight = (watching && !rs.expRow) || (stepOneInFlight && rs.rungsConfirmed && !rs.expRow);
  // The comparison is running: the reading is on screen but not yet answerable.
  const checkInFlight = (watching || stepOneInFlight) && rs.rungsConfirmed && !!rs.expRow && !rs.conflict;

  // The writer fixed a reading: whatever is still the model's below it was read assuming the old one.
  const onFixed = (facetKey, row) => {
    const othersProvisional = rs.rows.some(
      (r) => r.status === 'provisional' && r.id !== row.id && r.facet !== 'experience',
    );
    if (!othersProvisional) return;
    setRereading(true);
    post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: 'baseline', mode: 'reread', facet: facetKey }, { onFinish: () => setRereading(false) });
  };

  // Step 1 runs itself. Nothing on file for this scene → start reading it.
  useEffect(() => {
    if (needs_baseline && !running) {
      setRunning(true);
      post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: 'baseline', mode: 'run' }, { onFinish: () => setRunning(false) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needs_baseline, unitId]);

  // These two run themselves, one after the other, inside a single mount — so a `watching`
  // flag cannot guard them (the second would be skipped while the first is still finishing,
  // and its effect never re-runs). The server's flag says whether the run is still needed; the
  // ref only stops the same flag firing twice, and clears when the server says it is done.
  const fired = useRef({ exp: false, chk: false, unit: unitId });
  if (fired.current.unit !== unitId) fired.current = { exp: false, chk: false, unit: unitId };

  // The ladder is settled: read the same pages again, this time for what they do to us — and
  // without the ladder in context, so the reading is able to disagree with it.
  useEffect(() => {
    if (!needs_experience) { fired.current.exp = false; return; }
    if (fired.current.exp) return;
    fired.current.exp = true;
    setWatching(true);
    post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: 'baseline', mode: 'experience' }, { onFinish: () => setWatching(false) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needs_experience, unitId]);

  // Both readings are in: can they both be true of the same scene?
  useEffect(() => {
    if (!needs_conflict_check) { fired.current.chk = false; return; }
    if (fired.current.chk) return;
    fired.current.chk = true;
    setWatching(true);
    post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: 'baseline', mode: 'conflict' }, { onFinish: () => setWatching(false) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needs_conflict_check, unitId]);

  // A run started elsewhere (another tab, a reload mid-run): watch for it to land.
  useEffect(() => {
    if (!inFlight) return undefined;
    const id = setInterval(() => router.reload({ preserveScroll: true }), 6000);
    return () => clearInterval(id);
  }, [inFlight]);

  // Header: step number + verb. While checking readings, it's still step 1.
  const headNumber = checking ? 1 : number;
  const headVerb = checking
    ? (rs.conflict ? 'Two readings of this scene disagree'
      : rereadInFlight ? 'Re-reading the scene…'
        : expInFlight ? 'Watching the scene…' : 'Check the reading')
    : (needs_baseline || working) && current === 'baseline' ? 'Reading the scene as written…' : info?.verb || next.label;
  const headChip = checking ? 'Baseline' : current_label || next.label;
  const proposed = ledger.proposed;
  const groups = STAGE_GROUPS[current] || [];
  const grouped = groups.map((g) => ({ ...g, rows: proposed.filter((r) => g.types.includes(r.type)) }));
  const groupedIds = new Set(grouped.flatMap((g) => g.rows.map((r) => r.id)));
  const rest = proposed.filter((r) => !groupedIds.has(r.id));
  const openRest = rest.filter((r) => r.kind === 'open');
  const otherRest = rest.filter((r) => r.kind !== 'open');
  const recommendedIds = proposed.filter((r) => r.recommended && r.kind !== 'open').map((r) => r.id);
  const acceptableIds = proposed.filter((r) => r.kind !== 'open').map((r) => r.id);
  const exclusive = groups.some((g) => g.exclusive);

  const run = (mode = 'run') => {
    if (running) return;
    setRunning(true);
    post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: mode === 'another' ? 'approach' : next.stage, mode }, { onFinish: () => setRunning(false) });
  };

  const acceptMany = (ids) => {
    if (!ids.length) return;
    setBusy('many');
    post(`/srs/p/${uuid}/u/${unitId}/accept`, { ids, provisional }, {
      onSuccess: () => onLocked?.({ stage: current, title: `${ids.length} rows` }, provisional ? 'Accepted as provisional' : 'Locked'),
      onFinish: () => setBusy(null),
    });
  };

  const reading = (needs_baseline || working) && current === 'baseline';

  return (
    <section className="srs-now">
      <div className="srs-now-head">
        <div className="srs-step-no">Step {headNumber} of {total}{next.kind === 'done' ? ' · done' : ''}</div>
        <h2 className="srs-now-title">
          {next.kind === 'done' ? 'This scene is done' : headVerb}
          {headChip && <span className="srs-spec">{headChip}</span>}
        </h2>
        {next.kind === 'done' ? (
          <div className="srs-now-why">{next.why}</div>
        ) : checking ? (
          <div className="srs-explain">
            <div>
              <span className="srs-explain-k">Your job</span>
              {rs.conflict
                ? 'One of the two readings below is wrong about this scene. You decide which one moves.'
                : rs.activeIndex !== -1
                  ? `Reading ${rs.activeIndex + 1} of ${rs.rungs.length + (rs.expFacet ? 1 : 0)} — is this what the scene does, as written? That's right, or fix it.`
                  : !rs.expRow
                    ? 'The three readings are confirmed. One more, read off the pages on its own.'
                    : `Last reading — is this what the scene does to us? That's right, or fix it.`}
              {' '}Facts, not decisions; nothing here is protected.
            </div>
          </div>
        ) : reading ? (
          <div className="srs-explain">
            <div><span className="srs-explain-k">What we're figuring out</span>{info?.what}</div>
            <div><span className="srs-explain-k">Why it comes first</span>{info?.why}</div>
            <div><span className="srs-explain-k">Your job</span>{info?.job}</div>
          </div>
        ) : info ? (
          <div className="srs-explain">
            <div><span className="srs-explain-k">What we're figuring out</span>{info.what}</div>
            <div><span className="srs-explain-k">Why it comes here</span>{info.why}</div>
            <div><span className="srs-explain-k">Your job</span>{proposed.length > 0 && decide_hint ? decide_hint : info.job}</div>
          </div>
        ) : (
          <div className="srs-now-why">{next.why}</div>
        )}
      </div>

      {reading && (
        <div className="d-flex align-items-center gap-3 mt-3">
          <span className="spinner-border text-primary" role="status" aria-label="reading" />
          <span className="text-muted">Reading the pages below. Usually 20–60 seconds.</span>
        </div>
      )}

      {checking && (
        <div className="mt-2">
          <Readings ledger={ledger} facets={readingFacets} uuid={uuid} unitId={unitId} busy={busy} setBusy={setBusy}
            onDiscuss={onDiscuss} onLocked={onLocked} onFixed={onFixed} rereading={rereadInFlight}
            conflictLabels={conflictLabels} experienceRunning={expInFlight} checkRunning={checkInFlight}
            onExperience={() => { if (!watching) { setWatching(true); post(`/srs/p/${uuid}/u/${unitId}/step`, { stage: 'baseline', mode: 'experience' }, { onFinish: () => setWatching(false) }); } }}
            canSpan={active.grain === 'scene' && !!nextUnit}
            onSpan={() => post(`/srs/p/${uuid}/u/${unitId}/span`, {})} />
          {read && read.content && !/^\(no prose/.test(read.content) && (
            <details className="srs-details mt-3">
              <summary className="srs-label">The model's read</summary>
              <div className="srs-read-body">{read.content}</div>
            </details>
          )}
        </div>
      )}

      {!checking && !reading && current === 'target' && rs.rungsConfirmed && rs.rungs[0]?.row && (
        <div className="srs-criteria">
          <div className="srs-criteria-head">As written, it's for <span className="srs-spec">confirmed at step 1</span></div>
          <div className="srs-criteria-body">{rs.rungs[0].row.body}</div>
          <button type="button" className="btn btn-sm btn-link p-0 mt-1" onClick={() => onShowStage('baseline')}>the other readings</button>
        </div>
      )}
      {!checking && !reading && <CriteriaStrip criteria={criteria} stageWriterLabel={info?.verb || current_label} />}

      {!checking && !reading && read && read.content && !/^\(no prose/.test(read.content) && (
        <div className="srs-read">
          <div className="srs-label mb-1">The model's read</div>
          <div className="srs-read-body">{read.content}</div>
          {read.errors?.length > 0 && <div className="srs-meta text-danger mt-1">{read.errors.join(' ')}</div>}
        </div>
      )}

      {!checking && !reading && next.kind === 'model' && (
        <div className="d-flex align-items-center gap-3 mt-3">
          <button className="btn btn-primary btn-lg" onClick={() => run()} disabled={working}>
            {working ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status" aria-label="running" />Working on step {number}…</>
            ) : (
              <>Start step {number}: {(info?.verb || next.label).toLowerCase()}</>
            )}
          </button>
          {working && <span className="srs-meta">{inFlight ? 'Still running from before — this page refreshes itself when it lands.' : 'One model call — usually 20–90 seconds.'}</span>}
        </div>
      )}

      {next.kind === 'done' && (
        <div className="d-flex align-items-center gap-3 mt-3">
          {nextUnit
            ? <Link href={`/srs/p/${uuid}/${nextUnit.id}`} className="btn btn-primary btn-lg">Next scene: {nextUnit.label} →</Link>
            : <span className="text-muted">This was the last scene.</span>}
        </div>
      )}

      {next.kind === 'resolve' && (
        <p className="text-muted mt-2 mb-0">The blocking open items are under Settled → Open items. Resolve them, or untick "Render must wait for this".</p>
      )}

      {proposed.length > 0 && (
        <div className="mt-3">
          {grouped.map((g) => (
            <div key={g.title} className="srs-group">
              <div className="srs-group-head">
                <span className="srs-group-title">{g.title}</span>
                {g.hint && <span className="srs-group-hint">({g.hint})</span>}
              </div>
              {g.rows.length === 0 && g.emptyText && <p className="text-muted mb-2">{g.emptyText}</p>}
              <div className={g.exclusive ? 'srs-candidates' : ''}>
                {g.rows.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onLocked={onLocked} choose={!!g.exclusive} />)}
              </div>
              {g.exclusive && (
                <div className="mt-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => run('another')} disabled={working}>
                    {working ? 'Working…' : 'Another option'}
                  </button>
                </div>
              )}
            </div>
          ))}
          {otherRest.length > 0 && (
            <div className="srs-group">
              <div className="srs-group-head"><span className="srs-group-title">Also proposed</span></div>
              {otherRest.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onLocked={onLocked} />)}
            </div>
          )}
          {openRest.length > 0 && (
            <div className="srs-group">
              <div className="srs-group-head"><span className="srs-group-title">Open questions the model raised</span></div>
              {openRest.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onLocked={onLocked} />)}
            </div>
          )}

          {!exclusive && acceptableIds.length > 1 && (
            <div className="srs-batch d-flex flex-wrap align-items-center gap-3 mt-2">
              {recommendedIds.length > 0 && (
                <button className="btn btn-primary" disabled={busy === 'many'} onClick={() => acceptMany(recommendedIds)}>
                  Accept recommended ({recommendedIds.length})
                </button>
              )}
              <button className="btn btn-outline-primary" disabled={busy === 'many'} onClick={() => acceptMany(acceptableIds)}>
                Accept all ({acceptableIds.length})
              </button>
              <div className="form-check mb-0">
                <input className="form-check-input" type="checkbox" id="srs-batch-prov" checked={provisional} onChange={(e) => setProvisional(e.target.checked)} />
                <label className="form-check-label" htmlFor="srs-batch-prov">as provisional instead</label>
              </div>
            </div>
          )}
        </div>
      )}

      {!reading && !checking && next.kind !== 'done' && (
        <div className="mt-3">
          <AddRow types={types} uuid={uuid} unitId={unitId} defaultType={ADD_DEFAULT[current] || 'story_constraint'} />
        </div>
      )}
    </section>
  );
}

/* ───────────────────────────── SETTLED pane ───────────────────────────── */

function SettledPane({ ledger, parent_locks, parent_open = [], active, uuid, path, expanded, busy, setBusy, onDiscuss, readingFacets, hideStage = null }) {
  const binding = [...ledger.locked, ...ledger.provisional];
  const stages = path
    .filter((s) => s.key !== hideStage)
    .map((s) => ({ ...s, rows: binding.filter((r) => r.stage === s.key) }))
    .filter((s) => s.rows.length > 0);
  // Default to the most recently completed stage open — usually the one the current step rests on.
  const defaultOpen = expanded ?? (stages.length ? stages[stages.length - 1].key : null);
  const floating = binding.filter((r) => !r.stage);
  const setAside = ledger.history.filter((r) => r.type === 'mechanism' && r.status === 'cancelled');
  const otherHistory = ledger.history.filter((r) => !(r.type === 'mechanism' && r.status === 'cancelled'));
  const contract = parent_locks.filter((r) => r.type === 'story_constraint' && r.facet);
  const otherInherited = parent_locks.filter((r) => !(r.type === 'story_constraint' && r.facet));

  return (
    <section className="srs-settled">
      <h2 className="h6 srs-label mb-2">Decided so far</h2>

      {stages.map((s) => {
        const prov = s.rows.filter((r) => r.status === 'provisional').length;
        const isEvidence = s.key === 'baseline';
        const confirmedReadings = s.rows.filter((r) => r.status === 'locked').length;
        const count = isEvidence
          ? `${s.rows.length} reading${s.rows.length === 1 ? '' : 's'}${confirmedReadings ? ` · ${confirmedReadings} confirmed` : ''}`
          : prov === s.rows.length ? `${s.rows.length} provisional` : `${s.rows.length - prov} decided${prov ? ` · ${prov} provisional` : ''}`;
        return (
          <details key={`${s.key}-${defaultOpen === s.key}`} className="srs-details" open={defaultOpen === s.key}>
            <summary className="srs-label"><StageName s={s} /> · {count}</summary>
            {isEvidence
              ? <Readings mode="settled" ledger={ledger} facets={readingFacets} uuid={uuid} unitId={active.id} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} />
              : s.rows.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} compact />)}
          </details>
        );
      })}

      {floating.length > 0 && (
        <details className="srs-details" open>
          <summary className="srs-label">Notes & decisions · {floating.length}</summary>
          {floating.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} compact />)}
        </details>
      )}

      {ledger.open.length > 0 && (
        <details className="srs-details" open>
          <summary className="srs-label">Open questions · {ledger.open.length}</summary>
          {ledger.open.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} compact />)}
        </details>
      )}

      {parent_open.length > 0 && (
        <details className="srs-details" open>
          <summary className="srs-label">Tensions carried from the script · {parent_open.length}</summary>
          <div className="srs-meta mb-2">Where a contract choice and the pages disagree — recorded when the choice was made, waiting here.</div>
          {parent_open.map((r) => (
            <div key={r.id} className="srs-card compact">
              {r.title && <div className="fw-semibold">{r.title}</div>}
              <div className="srs-body">{r.body}</div>
              {r.anchored_to && <div className="srs-anchor mt-1">scenes: {r.anchored_to}</div>}
              <div className="srs-meta mt-1">{r.unit_label} · <Link href={`/srs/p/${uuid}/contract`} className="text-muted">contract</Link></div>
            </div>
          ))}
        </details>
      )}

      {contract.length > 0 && (
        <details className="srs-details">
          <summary className="srs-label">The script's contract · {contract.length} <span className="srs-spec">inherited</span></summary>
          {contract.map((r) => (
            <div key={r.id} className="srs-card compact">
              <div className="fw-semibold">{r.facet === 'arc' || r.facet === 'world_rule' ? r.title : (r.facet || '').replace(/_/g, ' ')}</div>
              <div className="srs-body">{r.body}</div>
              <div className="srs-meta mt-1">{r.status === 'locked' ? 'approved' : 'drafted — not yet approved'} · <Link href={`/srs/p/${uuid}/contract`} className="text-muted">contract</Link></div>
            </div>
          ))}
        </details>
      )}

      {otherInherited.length > 0 && (
        <details className="srs-details">
          <summary className="srs-label">Inherited from the sequence and act · {otherInherited.length}</summary>
          {otherInherited.map((r) => (
            <div key={r.id} className="srs-card compact">
              <div className="d-flex align-items-start gap-2">
                <span className="srs-type">{r.type_label}</span>
                <div className="flex-grow-1">
                  {r.title && <div className="fw-semibold">{r.title}</div>}
                  <div className="srs-body">{r.body}</div>
                  <div className="srs-meta mt-1">
                    v{r.version} · {r.status} · <Link href={`/srs/p/${uuid}/${r.unit_id}`} className="text-muted">{r.unit_label}</Link> · #{r.id}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </details>
      )}

      {setAside.length > 0 && (
        <details className="srs-details">
          <summary className="srs-label">Set aside · {setAside.length} mechanism{setAside.length === 1 ? '' : 's'}</summary>
          {setAside.map((r) => <Card key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} compact />)}
        </details>
      )}

      {otherHistory.length > 0 && (
        <details className="srs-details">
          <summary className="srs-label">History · {otherHistory.length}</summary>
          {otherHistory.map((r) => (
            <div key={r.id} className="srs-card compact">
              <div className="d-flex align-items-start gap-2">
                <span className="srs-type">{r.type_label}</span>
                <div className="flex-grow-1">
                  {r.title && <div className="fw-semibold">{r.title}</div>}
                  <div className="srs-body text-muted">{r.body}</div>
                  <div className="srs-meta mt-1">v{r.version} · {r.status}{r.resolution ? ` · resolved: ${r.resolution}` : ''} · #{r.id}</div>
                </div>
              </div>
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

/* ───────────────────────────── DISCUSS ───────────────────────────── */

function Discuss({ messages, about, setAbout, uuid, unitId, inputRef, pinned = false }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  // Conversation only: step runs and their replies live in the Now pane.
  const shown = messages.filter((m) => !(m.meta?.step));

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [shown.length, sending]);

  const send = (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    post(`/srs/p/${uuid}/u/${unitId}/messages`, { message: msg, about: about?.id ?? null }, {
      onSuccess: () => setText(''),
      onFinish: () => { setSending(false); inputRef.current?.focus(); },
    });
  };

  const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(e); };

  const placeholder = about
    ? `Push back on "${about.title || about.type_label}", ask for a variant, or say what it's missing…`
    : 'Ask, disagree, or redirect…';

  return (
    <section className={`srs-discuss${pinned ? ' pinned' : ''}`}>
      <h2 className="h6 srs-label mb-1">Discuss</h2>
      {about ? (
        <div className="srs-about">
          <span>about: <strong>{about.title || about.type_label}</strong>{about.recommended ? ' · ★ recommended' : ''}</span>
          <button type="button" className="btn btn-sm btn-link p-0 ms-2" onClick={() => setAbout(null)} aria-label="Stop discussing this row">✕</button>
        </div>
      ) : (
        <div className="small text-muted mb-2">Disagree with something, or want it explained? Say so. To talk about one row, use its Discuss button.</div>
      )}

      <div className="srs-discuss-list" ref={listRef}>
        {shown.map((m) => (
          <div key={m.id} className={`srs-msg ${m.role}`}>
            <div className="srs-who">
              {m.role === 'user' ? 'You' : m.role === 'assistant' ? 'Model' : 'System'}
              {m.about?.title ? <span className="fw-normal text-muted"> · about "{m.about.title}"</span> : null}
            </div>
            {m.content}
            {m.role === 'assistant' && m.proposals?.items?.length > 0 && (() => {
              const made = m.proposals.items.filter((p) => !p.withdrew).length;
              const withdrew = m.proposals.items.filter((p) => p.withdrew);
              return (
                <div className="srs-proposed-note">
                  {made > 0 && <>Proposed {made} row{made === 1 ? '' : 's'} — they're in Now. </>}
                  {withdrew.length > 0 && <>Withdrew {withdrew.map((w) => `#${w.withdrew}`).join(', ')}.</>}
                </div>
              );
            })()}
            {m.meta?.errors?.length > 0 && <div className="srs-proposed-note text-danger">{m.meta.errors.join(' ')}</div>}
          </div>
        ))}
        {sending && (
          <div className="srs-msg assistant text-muted">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-label="thinking" />Thinking…
          </div>
        )}
      </div>

      <form onSubmit={send} className="mt-2">
        <textarea ref={inputRef} className="form-control srs-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey} placeholder={placeholder} disabled={sending} />
        <div className="d-flex justify-content-end mt-2">
          <button type="submit" className="btn btn-outline-primary" disabled={sending || !text.trim()}>Send</button>
        </div>
      </form>
    </section>
  );
}

/* ───────────────────────────── page ───────────────────────────── */

export default function Workspace({ project, tree, active, parent_locks, parent_open = [], ledger, step, messages, types, reading_facets = [], conflict_labels = {}, kernel_version }) {
  const { flash } = usePage().props;
  const [busy, setBusy] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [about, setAbout] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const inputRef = useRef(null);

  const nextUnit = active.neighbors?.next;
  const prevUnit = active.neighbors?.prev;

  // A new scene: drop any row focus from the previous one.
  useEffect(() => { setAbout(null); setExpanded(null); setDrawer(false); }, [active.id]);

  // The focused row left the screen (accepted, rejected, set aside): unpin it.
  const visibleIds = useMemo(() => new Set([...ledger.proposed, ...ledger.locked, ...ledger.provisional, ...ledger.open].map((r) => r.id)), [ledger]);
  useEffect(() => { if (about && !visibleIds.has(about.id)) setAbout(null); }, [about, visibleIds]);

  const crumbs = active.parents.filter((p) => p.grain !== 'script');

  // Progressive disclosure: nothing appears before it has content.
  const checking = isChecking(step, ledger, reading_facets);
  const reading = (step.needs_baseline || step.run_in_flight === 'baseline') && step.current === 'baseline';
  const decidedRows = [...ledger.locked, ...ledger.provisional].filter((r) => !(checking && r.type === 'current_state'));
  const showSettled = decidedRows.length > 0 || ledger.open.length > 0 || ledger.history.length > 0 || parent_locks.length > 0 || parent_open.length > 0;
  const showDiscuss = !reading && (messages.some((m) => !m.meta?.step) || decidedRows.length > 0 || ledger.proposed.length > 0 || checking);

  const onDiscuss = (row) => {
    setAbout({ id: row.id, title: row.title, type_label: row.type_label, recommended: row.recommended });
    // The Discuss panel moves to the top of the sticky column when a row is pinned — no scroll jump.
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  };

  const onPickStage = (key) => setExpanded((cur) => (cur === key ? null : key));
  const onShowStage = (key) => setExpanded(key);

  // Feedback on a lock: say what happened and open the group it landed in.
  const onLocked = (row, verb) => {
    toast.success(`${verb}: ${row.title || row.type_label || 'row'}`, { autoClose: 2500 });
    if (row.stage) setExpanded(row.stage);
  };

  return (
    <div className="srs srs-work container-fluid py-3">
      <Head title={`${active.label} · ${project.title || 'Revision'}`} />
      {flash?.error && <div className="alert alert-danger py-2">{flash.error}</div>}
      {drawer && <ScenesDrawer tree={tree} activeId={active.id} uuid={project.uuid} onClose={() => setDrawer(false)} />}

      <header className="srs-head">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDrawer(true)}>‹ Scenes</button>
          <span className="text-muted small">
            <Link href={`/srs/p/${project.uuid}/contract`} className="text-muted">{project.title}</Link>
            {crumbs.map((p) => (
              <span key={p.id}> › <Link href={`/srs/p/${project.uuid}/${p.id}`} className="text-muted">{p.label}</Link></span>
            ))}
          </span>
          <span className="ms-auto text-muted small">
            {prevUnit && <Link href={`/srs/p/${project.uuid}/${prevUnit.id}`} className="text-muted">‹ {prevUnit.label}</Link>}
            {prevUnit && nextUnit && <span className="mx-2">·</span>}
            {nextUnit && <Link href={`/srs/p/${project.uuid}/${nextUnit.id}`} className="text-muted">{nextUnit.label} ›</Link>}
            {active.grain === 'scene' && nextUnit && (
              <button type="button" className="btn btn-link btn-sm text-muted p-0 ms-3" onClick={() => post(`/srs/p/${project.uuid}/u/${active.id}/span`, {})}>
                read with the next scene as one unit
              </button>
            )}
          </span>
        </div>
        <h1 className="h4 mt-2 mb-1">{active.label}</h1>
        <PathStrip path={step.path} onPick={onPickStage} checking={checking} />
      </header>

      <SceneContext active={active} ledger={ledger} facets={reading_facets} uuid={project.uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} />

      <div className="row g-4 mt-1">
        <div className={showSettled || showDiscuss ? 'col-12 col-lg-7' : 'col-12 col-lg-9'}>
          <NowPane step={step} ledger={ledger} active={active} uuid={project.uuid} unitId={active.id} nextUnit={nextUnit}
            types={types} readingFacets={reading_facets} conflictLabels={conflict_labels}
            busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onLocked={onLocked} onShowStage={onShowStage} />
        </div>
        {(showSettled || showDiscuss) && (
          <div className="col-12 col-lg-5">
            <div className="srs-side">
              {about && showDiscuss && (
                <Discuss messages={messages} about={about} setAbout={setAbout} uuid={project.uuid} unitId={active.id} inputRef={inputRef} pinned />
              )}
              {showSettled && (
                <SettledPane ledger={ledger} parent_locks={parent_locks} parent_open={parent_open} active={active} uuid={project.uuid}
                  path={step.path} expanded={expanded} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} readingFacets={reading_facets}
                  hideStage={checking ? 'baseline' : null} />
              )}
              {!about && showDiscuss && (
                <Discuss messages={messages} about={about} setAbout={setAbout} uuid={project.uuid} unitId={active.id} inputRef={inputRef} />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="text-end text-muted small mt-3">kernel v{kernel_version}</div>
      <ToastContainer position="bottom-right" newestOnTop hideProgressBar />
    </div>
  );
}
